import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@design-system';
import { Info, Mail, User } from 'lucide-react';
import {
  StageCell,
  ColumnHeader,
  ClientNameCell,
  ReferralSourceCell,
  FilterButton,
} from '@/features/clients-table';

import { isPlaceholderEmail } from '@shared';
import { createColumnHelper } from '@tanstack/react-table';

import { ClientActionMenu } from '@/shared/components/client-action-menu';
import { ActionTags } from '@/shared/components/action-tags';

import type { ModalState } from '@client';

const columnHelper = createColumnHelper<Client>();

interface ColumnsConfig {
  data: Client[];
  sessionAdvisor: Advisor;
  subordinateAdvisors: Advisor[];
  setModalState: React.Dispatch<React.SetStateAction<ModalState>>;
}

function getClientAdvisor(
  client: Client,
  sessionAdvisor: Advisor,
  subordinateAdvisors: Advisor[],
): Advisor {
  if (client.advisorId === sessionAdvisor.advisorId) return sessionAdvisor;
  return subordinateAdvisors.find(a => a.advisorId === client.advisorId) ?? sessionAdvisor;
}

function getAdvisorOrganization(
  advisorId: string,
  subordinateAdvisors: Advisor[],
  sessionAdvisor: Advisor,
): string {
  if (advisorId === sessionAdvisor.advisorId) {
    return sessionAdvisor.organizationDisplayName || sessionAdvisor.organizationName || '';
  }
  const advisor = subordinateAdvisors.find(a => a.advisorId === advisorId);
  return advisor?.organizationDisplayName || advisor?.organizationName || '';
}

export function createClientsTableColumns({
  data,
  setModalState,
  sessionAdvisor,
  subordinateAdvisors,
}: ColumnsConfig) {
  return [
    // Client column: name + sample badge + coupled subtitle
    columnHelper.accessor(row => `${row.clientFirstName} ${row.clientLastName}`, {
      id: 'clientName',
      header: ({ column }) => (
        <ColumnHeader
          column={column}
          title="Client"
        />
      ),
      cell: info => {
        const client = info.row.original;

        return (
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <ClientNameCell
                  clients={data}
                  client={client}
                  sessionAdvisor={sessionAdvisor}
                />
              </div>
            </div>
            <div className="opacity-0 transition-opacity group-hover:opacity-100">
              <ClientActionMenu
                client={client}
                sessionAdvisor={sessionAdvisor}
                setModalState={setModalState}
              />
            </div>
          </div>
        );
      },
      enableHiding: false,
      size: 260,
    }),

    // Demographics column: age · gender · state
    columnHelper.accessor(
      row => {
        const d = row.clientDemographics;
        if (!d) return '';
        const parts: string[] = [];
        if (d.age != null) parts.push(String(d.age));
        if (d.gender) parts.push(d.gender);
        if (d.stateAbbreviation) parts.push(d.stateAbbreviation);
        return parts.join(' · ');
      },
      {
        id: 'demographics',
        header: () => (
          <span className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
            Demographics
          </span>
        ),
        cell: info => {
          const compact = info.getValue();
          if (!compact) return <span className="text-muted-foreground">—</span>;
          return <span className="text-foreground text-sm">{compact}</span>;
        },
        size: 160,
      },
    ),

    // Organization column
    columnHelper.accessor(
      row => getAdvisorOrganization(row.advisorId, subordinateAdvisors, sessionAdvisor),
      {
        id: 'organization',
        header: ({ column, table }) => {
          const values = Array.from(
            new Set(
              table.getPreFilteredRowModel().rows.map(row => row.getValue<string>('organization')),
            ),
          ).sort((a, b) => (a ?? '').toLowerCase().localeCompare((b ?? '').toLowerCase()));
          const filterValue = (column.getFilterValue() as string[]) ?? [];
          return (
            <div className="flex items-center gap-1">
              <span className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                Organization
              </span>
              <FilterButton
                values={values}
                selectedValues={filterValue}
                onChange={v => column.setFilterValue(v.length ? v : undefined)}
                renderValue={v => v || 'Undefined'}
              />
            </div>
          );
        },
        cell: info => {
          const client = info.row.original;
          const organization = info.getValue();

          if (client.advisorId === sessionAdvisor.advisorId) {
            return <span className="text-foreground text-sm">{organization}</span>;
          }

          const advisor = subordinateAdvisors.find(a => a.advisorId === client.advisorId);
          if (!advisor) {
            return <span className="text-foreground text-sm">{organization}</span>;
          }

          return (
            <HoverCard>
              <HoverCardTrigger asChild>
                <span className="flex cursor-pointer items-center gap-1">
                  <span className="text-foreground text-sm">{organization}</span>
                  <Info className="text-primary size-3.5 shrink-0" />
                </span>
              </HoverCardTrigger>
              <HoverCardContent className="w-auto max-w-80 p-4">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Advisor Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <User className="text-primary size-4" />
                      <span>
                        {`${advisor.advisorFirstName || ''} ${advisor.advisorLastName || ''}`.trim()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="text-primary size-4" />
                      <span>{advisor.advisorEmail}</span>
                    </div>
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>
          );
        },
        enableHiding: true,
        filterFn: (row, _columnId, filterValue: (string | undefined)[]) => {
          if (!filterValue || filterValue.length === 0) return true;
          return filterValue.includes(row.getValue('organization'));
        },
      },
    ),

    // Statuses column
    columnHelper.display({
      id: 'statuses',
      header: ({ column, table }) => {
        const values = Array.from(
          new Set(table.getPreFilteredRowModel().rows.flatMap(row => row.original.clientTags)),
        ).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        const filterValue = (column.getFilterValue() as string[]) ?? [];
        return (
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
              Statuses
            </span>
            <FilterButton
              values={values}
              selectedValues={filterValue}
              onChange={v => column.setFilterValue(v.length ? v : undefined)}
            />
          </div>
        );
      },
      cell: ({ row }) => (
        <ActionTags
          clientTags={row.original.clientTags}
          client={row.original}
        />
      ),
      filterFn: (row, _columnId, filterValue: string[]) => {
        if (!filterValue || filterValue.length === 0) return true;
        return filterValue.some(tag => row.original.clientTags.includes(tag));
      },
    }),

    // Relationship stage column
    columnHelper.accessor(row => row.clientRelationshipStage, {
      id: 'stage',
      header: ({ column, table }) => {
        const values = Array.from(
          new Set(
            table.getPreFilteredRowModel().rows.map(row => row.original.clientRelationshipStage),
          ),
        ).sort((a, b) =>
          (a ?? '')
            .toString()
            .toLowerCase()
            .localeCompare((b ?? '').toString().toLowerCase()),
        );
        const filterValue =
          (column.getFilterValue() as (ClientRelationshipStage | undefined)[]) ?? [];
        return (
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
              Stage
            </span>
            <FilterButton
              values={values}
              selectedValues={filterValue}
              onChange={v => column.setFilterValue(v.length ? v : undefined)}
              renderValue={v => v ?? 'Undefined'}
            />
          </div>
        );
      },
      cell: info => <StageCell client={info.row.original} />,
      filterFn: (row, _columnId, filterValue: (ClientRelationshipStage | undefined)[]) => {
        if (!filterValue || filterValue.length === 0) return true;
        return filterValue.includes(row.original.clientRelationshipStage);
      },
    }),

    // Email column
    columnHelper.accessor('clientEmail', {
      id: 'email',
      header: ({ column }) => (
        <ColumnHeader
          column={column}
          title="Email"
        />
      ),
      cell: info => {
        const email = info.getValue();
        return (
          <span className="text-foreground text-sm">{isPlaceholderEmail(email) ? '' : email}</span>
        );
      },
    }),

    // Added date column
    columnHelper.accessor(row => row.clientAddedDateTime, {
      id: 'addedDate',
      header: ({ column }) => (
        <ColumnHeader
          column={column}
          title="Added"
        />
      ),
      cell: info => {
        const date = info.getValue();
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-foreground text-sm">{date.toLocaleDateString()}</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{date.toLocaleTimeString()}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    }),

    // Referral source column
    columnHelper.accessor(row => row.clientReferralSourceId, {
      id: 'source',
      header: ({ column, table }) => {
        const labelMap = new Map<string | undefined, string>();
        table.getPreFilteredRowModel().rows.forEach(row => {
          const client = row.original;
          const id = client.clientReferralSourceId;
          if (!labelMap.has(id)) {
            const advisor = getClientAdvisor(client, sessionAdvisor, subordinateAdvisors);
            const source = advisor?.referralSources?.find(s => s.advisorReferralSourceId === id);
            labelMap.set(id, source?.referralSourceLabel ?? 'Undefined');
          }
        });
        const values = Array.from(labelMap.keys()).sort((a, b) =>
          (labelMap.get(a) ?? '')
            .toLowerCase()
            .localeCompare((labelMap.get(b) ?? '').toLowerCase()),
        );
        const filterValue = (column.getFilterValue() as (string | undefined)[]) ?? [];
        return (
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
              Source
            </span>
            <FilterButton
              values={values}
              selectedValues={filterValue}
              onChange={v => column.setFilterValue(v.length ? v : undefined)}
              renderValue={v => labelMap.get(v) ?? 'Undefined'}
            />
          </div>
        );
      },
      cell: info => {
        const client = info.row.original;
        const advisor = getClientAdvisor(client, sessionAdvisor, subordinateAdvisors);
        return (
          <ReferralSourceCell
            client={client}
            advisor={advisor}
            disabled={client.advisorId !== sessionAdvisor.advisorId}
          />
        );
      },
      filterFn: (row, _columnId, filterValue: (string | undefined)[]) => {
        if (!filterValue || filterValue.length === 0) return true;
        return filterValue.includes(row.original.clientReferralSourceId);
      },
    }),
  ];
}
